import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useBoolean, useSetState } from 'react-use'
import { hexFromArgb, themeFromImage } from '@material/material-color-utilities'

import { TrashAPI } from '@makyoapp/makyo-sdk'
import { useFormEnter } from '@/shared/makyo-hooks'
import {
  AvatarCell,
  AvatarSize,
  Field,
  Label,
  ModalGrid,
  SelectDropdown,
  SimpleInput,
  UploadEasyCrop,
  UploadInput,
} from '@makyoapp/makyo-ui'

import { appConfig } from '@/configs'
import { useOrgsContext } from '@/context/OrgsContext'
import { BASE_PALETTE_COLOR, Theme } from '@/shared/brand-kit'
import { cleanUpString } from '@/shared/utils'

import { BrandColorOption } from '../BrandColorOption'
import { ColorStrip } from '../ColorStrip'

export interface NewOrgTeamModalSubmit {
  name: string
  avatar?: File
  orgId: string
  src: string
  brandKit?: {
    type: string
    color: string
    image: {
      src: string
      file?: File
    }
  }
}

interface NewOrgTeamModalFormProps {
  show: boolean
  isTeam?: boolean
  formId: string
  onSubmit: (value: NewOrgTeamModalSubmit, done: () => void) => void
  onHide?: () => void
}

function getTitle(isTeam: boolean, step: number): string {
  if (isTeam) {
    return 'Create New Team'
  }

  switch (step) {
    case 1:
      return 'Create New Organization'
    case 2:
      return 'Upload a logo'
    case 3:
    default:
      return 'New Brand Kit'
  }
}

export default function NewOrgTeamModalForm(props: NewOrgTeamModalFormProps) {
  const { show, formId, onSubmit, onHide } = props
  const { t } = useTranslation()
  const { orgs } = useOrgsContext()
  const [isLoading, setIsLoading] = useBoolean(false)
  const [isNameChecking, setIsNameChecking] = useBoolean(false)
  const [nameError, setNameError] = useState('')

  const [addItem, setAddItem] = useSetState<NewOrgTeamModalSubmit>({
    name: '',
    avatar: undefined,
    src: '',
    orgId: orgs[0]?.id,
  })

  const [step, setStep] = useState(1)

  const [brandKitSetting, setBrandKitSetting] = useSetState<{
    color: string
    image: {
      src: string
      file?: File
    }
  }>({
    color: BASE_PALETTE_COLOR[0].color,
    image: {
      src: '',
      file: undefined,
    },
  })

  // Validate both active orgs and trash
  const validateOrgName = useCallback(
    async (name: string) => {
      if (!name) return true

      setIsNameChecking(true)
      try {
        // Check active organizations
        const activeOrgs = orgs.some((org) => org.name.toLowerCase() === name.toLowerCase())
        if (activeOrgs) {
          setNameError(t('Organization name already exists.'))
          return false
        }

        // Check trashed organizations
        const trashedItems = await TrashAPI.ReqList()
        console.log('Trashed items:', trashedItems)

        const trashedOrg = trashedItems.find((item) => {
          return item._ItemType === 'organization' && item._ItemName.toLowerCase() === name.toLowerCase()
        })

        console.log('Found trashedOrg:', trashedOrg)

        if (trashedOrg) {
          setNameError(t('This organization name already exists in trash.'))
          return false
        }

        setNameError('')
        return true
      } catch (error) {
        console.error('Error validating organization name:', error)
        setNameError(t('Error validating organization name.'))
        return false
      } finally {
        setIsNameChecking(false)
      }
    },
    [orgs, t]
  )

  // Debounced validation
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (addItem.name) {
        validateOrgName(addItem.name)
      } else {
        setNameError('')
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [addItem.name, validateOrgName])

  // Handle input change with validation
  const handleNameInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const name = e.target.value
      setAddItem({
        name: name,
      })
    },
    [setAddItem]
  )

  const imgRef = useRef(null)
  const [brandKit, setBrandKit] = useState<Theme | null>(null)

  useEffect(() => {
    if (step > 1 && imgRef.current) {
      themeFromImage(imgRef.current)
        .then((res) => {
          setBrandKit(res as unknown as Theme)
          setBrandKitSetting({
            color: hexFromArgb(res.source),
          })
        })
        .catch((e) => {
          console.error('error reading image to brand kit theme', e)
        })
    }
  }, [step, setBrandKit, setBrandKitSetting, brandKitSetting.image, imgRef])

  const handleSubmit = useCallback(async () => {
    if (nameError) return
    setIsLoading(true)
    try {
      onSubmit(
        {
          ...addItem,
          brandKit: {
            type: step === 3 ? 'color' : 'image',
            ...brandKitSetting,
          },
        },
        () => {
          setIsLoading(false)
          setAddItem({
            name: '',
            avatar: undefined,
            src: '',
            orgId: '',
          })
          setBrandKitSetting({
            color: BASE_PALETTE_COLOR[0].color,
            image: {
              src: '',
              file: undefined,
            },
          })
          setBrandKit(null)
          setStep(1)
        }
      )
    } catch (error) {
      console.error('Error submitting form:', error)
    } finally {
      setIsLoading(false)
    }
  }, [addItem, step, brandKitSetting, onSubmit, nameError])

  const handleAddNew = useCallback(async () => {
    if (addItem.name && !nameError) {
      if (props.isTeam) {
        await handleSubmit()
      } else {
        if (step < 2) {
          setStep((prev) => prev + 1)
        } else {
          await handleSubmit()
        }
      }
    }
  }, [step, addItem.name, props.isTeam, handleSubmit, nameError])

  useFormEnter(formId, handleAddNew)

  const handleHide = () => {
    setIsLoading(false)
    setAddItem({
      name: '',
      avatar: undefined,
      src: '',
      orgId: orgs[0]?.id, // Reset orgId to the first organization in the list
    })
    setBrandKit(null)
    setStep(1)
    onHide?.()
  }

  useEffect(() => {
    let timeout: NodeJS.Timeout

    if (!!addItem.name) {
      timeout = setTimeout(() => {
        setAddItem((prev) => ({
          ...prev,
          name: cleanUpString(addItem.name),
        }))
      }, 1000)
    }

    return () => {
      clearTimeout(timeout)
    }
  }, [addItem.name, setAddItem])

  const nameCharCount = addItem.name.trim().length
  const validLength = nameCharCount >= appConfig.charConstraints.min && nameCharCount < appConfig.charConstraints.max

  // const stepOneButtonDisabled = !validLength
  // Update button disabled state to include name validation
  const stepOneButtonDisabled = !validLength || !!nameError || isNameChecking

  useEffect(() => {
    if (!show) {
      setBrandKitSetting({
        color: BASE_PALETTE_COLOR[0].color,
        image: {
          src: '',
          file: undefined,
        },
      })
      setBrandKit(null)
      setStep(1)
    }
  }, [show])

  const handleCancel = () => {
    if (step === 1) {
      handleHide()
    } else if (step === 2) {
      setStep(1)
    } else if (step === 3) {
      setStep(2)
    }
  }

  return (
    <ModalGrid
      modalId={`${formId}-modal`}
      show={show}
      onHide={handleHide}
      title={t(getTitle(Boolean(props.isTeam), step))}
      onConfirm={handleAddNew}
      leadingTitle={step === 1 ? t('Cancel') : t('Back')}
      disabled={stepOneButtonDisabled || isLoading}
      onCancel={handleCancel}
      submitId={`${formId}-save`}
      primaryTrailing={step === 1 && !props.isTeam ? t('Next') : t('Create')}
      isLoading={isLoading}
      prefixSubmitComponent={
        step === 2 && (
          <button
            className='mr-2'
            type='button'
            onClick={() => {
              setStep(3)
            }}
          >
            <Trans>Skip</Trans>
          </button>
        )
      }
    >
      <form
        id={formId}
        onSubmit={(e) => {
          e.preventDefault()
          handleAddNew()
        }}
      >
        <div className='modal-sm'>
          {step === 1 && (
            <>
              <Field>
                <Label required>{t('Name')}</Label>
                <SimpleInput
                  error={nameError}
                  id={`${formId}-name`}
                  tabIndex={1}
                  required={true}
                  placeholder={t('Please Enter Name')}
                  value={addItem.name}
                  type='text'
                  onChange={handleNameInput}
                  withCounterConfig={{
                    min: appConfig.charConstraints.min,
                    max: appConfig.charConstraints.max,
                  }}
                />
              </Field>
              {props.isTeam && (
                <Field>
                  <Label htmlFor='avatar'>{t('Avatar')}</Label>
                  <UploadEasyCrop
                    id={`${formId}-avatar`}
                    initialPreview={addItem.src}
                    onChange={(nextResult) => {
                      if (nextResult?.file) {
                        setAddItem({
                          avatar: nextResult.file,
                          src: nextResult.src,
                        })
                      }
                    }}
                    supportedExtensions={[]}
                  />
                </Field>
              )}
              {props.isTeam && (
                <Field>
                  <Label>{t('Organization')}</Label>
                  <SelectDropdown
                    id={`${formId}-org`}
                    withSearch={true}
                    value={orgs.find((opt) => opt.id === addItem.orgId)}
                    optionLabel={(opt) => opt.name}
                    renderOption={(opt) => {
                      return <AvatarCell {...opt} size={AvatarSize.XS} />
                    }}
                    onChange={(opt) => {
                      setAddItem({
                        orgId: opt.id,
                      })
                    }}
                    options={orgs}
                  />
                </Field>
              )}
            </>
          )}
          {step === 2 && !props.isTeam && (
            <>
              <div className='text-light'>
                <Trans>The logo will be used to create a brand kit for you.</Trans>
              </div>
              <Field isVertical>
                <UploadInput
                  previewLabel={brandKitSetting?.image.file?.name}
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (f) {
                      setBrandKitSetting({
                        image: {
                          src: URL.createObjectURL(f),
                          file: f,
                        },
                      })
                    }
                  }}
                />
                {brandKitSetting?.image.src && (
                  <div className='flex-c4 flex-col'>
                    <img ref={imgRef} src={brandKitSetting?.image.src} className='max-w-[40%]' />
                    {/*brandKit && <Preview isVertical theme={brandKit} />*/}
                    {brandKit && (
                      <div className='w-full'>
                        <div>
                          <Trans>Generated Colors</Trans>
                        </div>
                        <ColorStrip theme={brandKit} />
                      </div>
                    )}
                  </div>
                )}
              </Field>
            </>
          )}
          {step === 3 && !props.isTeam && (
            <>
              <div className='text-base mb-2'>
                <Trans>Select color palette to create your brand kit</Trans>
              </div>
              <div>
                <SelectDropdown
                  arrow={false}
                  toggleClassName='palette-dd'
                  value={BASE_PALETTE_COLOR.find((c) => c.color === brandKitSetting.color)}
                  onChange={(opt, e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setBrandKitSetting({
                      color: opt.color,
                    })
                  }}
                  options={BASE_PALETTE_COLOR}
                  optionLabel={(opt) => opt.name}
                  renderOption={(opt, inToggle) => {
                    return <BrandColorOption opt={opt} inToggle={inToggle} />
                  }}
                />
              </div>
            </>
          )}
        </div>
      </form>
    </ModalGrid>
  )
}
